import React, { useEffect, useRef, useState } from 'react';

const InteractiveTradingChart = ({ 
  pair, 
  initialEntry, 
  initialSL, 
  initialTP, 
  direction,
  riskAmount,
  priceAction,
  counterTradeInfo,
  onConfirm,
  onCancel,
  timeframe = "1D" // Add timeframe prop with default
}) => {
  console.log('🎯 INTERACTIVE CHART PROPS:', { 
    pair, 
    initialEntry, 
    initialSL, 
    initialTP, 
    riskAmount,
    riskAmountType: typeof riskAmount,
    riskAmountIsNaN: isNaN(riskAmount)
  });
  
  const chartContainerRef = useRef();
  const chartRef = useRef();
  const seriesRef = useRef();
  const entryLineRef = useRef();
  const slLineRef = useRef();
  const tpLineRef = useRef();
  const scalpEntryLineRef = useRef();
  const scalpSLLineRef = useRef();
  const scalpTPLineRef = useRef();
  
  const [entry, setEntry] = useState(initialEntry);
  const [stopLoss, setStopLoss] = useState(initialSL);
  const [takeProfit, setTakeProfit] = useState(initialTP);
  const [lotSize, setLotSize] = useState(0);
  
  // Use refs for dragging to avoid closure issues
  const entryRef = useRef(initialEntry);
  const stopLossRef = useRef(initialSL);
  const takeProfitRef = useRef(initialTP);
  const isDraggingRef = useRef(null);
  
  // Scalp trade state
  const [showScalpTrade, setShowScalpTrade] = useState(false);
  const [scalpEntry, setScalpEntry] = useState(0);
  const [scalpSL, setScalpSL] = useState(0);
  const [scalpTP, setScalpTP] = useState(0);
  const [scalpLotSize, setScalpLotSize] = useState(0);
  
  // Refs for scalp dragging
  const scalpEntryRef = useRef(0);
  const scalpSLRef = useRef(0);
  const scalpTPRef = useRef(0);
  const showScalpTradeRef = useRef(false);
  
  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenContainerRef = useRef();
  
  // Countdown timer state
  const [countdown, setCountdown] = useState('');
  
  // Sync refs with state
  useEffect(() => {
    entryRef.current = entry;
    stopLossRef.current = stopLoss;
    takeProfitRef.current = takeProfit;
  }, [entry, stopLoss, takeProfit]);
  
  useEffect(() => {
    scalpEntryRef.current = scalpEntry;
    scalpSLRef.current = scalpSL;
    scalpTPRef.current = scalpTP;
    showScalpTradeRef.current = showScalpTrade;
  }, [scalpEntry, scalpSL, scalpTP, showScalpTrade]);

  // Countdown timer logic
  useEffect(() => {
    const calculateCountdown = () => {
      const now = new Date();
      let nextCandleClose;
      
      if (timeframe === '1h') {
        // Next hour
        nextCandleClose = new Date(now);
        nextCandleClose.setHours(now.getHours() + 1, 0, 0, 0);
      } else if (timeframe === '4h') {
        // Next 4-hour mark (0, 4, 8, 12, 16, 20)
        const currentHour = now.getHours();
        const next4HourMark = Math.ceil((currentHour + 1) / 4) * 4;
        nextCandleClose = new Date(now);
        nextCandleClose.setHours(next4HourMark, 0, 0, 0);
        if (next4HourMark >= 24) {
          nextCandleClose.setDate(nextCandleClose.getDate() + 1);
          nextCandleClose.setHours(0, 0, 0, 0);
        }
      } else {
        // Daily - next midnight UTC
        nextCandleClose = new Date(now);
        nextCandleClose.setUTCHours(24, 0, 0, 0);
      }
      
      const diff = nextCandleClose - now;
      
      if (diff <= 0) {
        setCountdown('Closing now...');
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      if (timeframe === '1D') {
        setCountdown(`${hours}h ${minutes}m ${seconds}s until daily close`);
      } else if (timeframe === '4h') {
        setCountdown(`${hours}h ${minutes}m ${seconds}s until 4H close`);
      } else {
        setCountdown(`${minutes}m ${seconds}s until 1H close`);
      }
    };
    
    // Calculate immediately
    calculateCountdown();
    
    // Update every second
    const interval = setInterval(calculateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, [timeframe]);
  
  // CRITICAL: Recalculate lot size whenever entry or stopLoss changes
  useEffect(() => {
    console.log('🔄 LOT SIZE RECALC TRIGGERED:', { entry, stopLoss, riskAmount, pair });
    
    // Check for valid inputs
    const validRisk = parseFloat(riskAmount) || 0;
    if (!entry || !stopLoss || validRisk <= 0 || entry === stopLoss) {
      console.warn('⚠️ INVALID INPUTS FOR LOT SIZE:', { entry, stopLoss, riskAmount, validRisk, entryEqualsStopLoss: entry === stopLoss });
      return;
    }
    
    const slDistance = Math.abs(entry - stopLoss);
    
    // CRYPTO PAIRS - Different calculation (no pips, direct price-based)
    const isCrypto = pair.includes('BTC') || pair.includes('ETH') || pair.includes('SOL') || 
                     pair.includes('XRP') || pair.includes('ADA') || pair.includes('DOT');
    
    if (isCrypto) {
      console.log('🪙 CRYPTO PAIR DETECTED:', pair);
      
      // BETTER CRYPTO FORMULA - Uses percentage-based approach
      // 1. Calculate SL as percentage of entry
      // 2. Position Size (USD) = Risk / SL%
      // 3. Lot Size (crypto amount) = Position Size / Entry Price
      
      const slPercent = slDistance / entry; // SL distance as % of entry
      const positionSizeUSD = validRisk / slPercent; // USD position size needed
      const calculatedLotSize = positionSizeUSD / entry; // Convert to crypto amount
      
      // Round to reasonable precision (0.001 for most crypto)
      const newLotSize = Math.max(0.001, Math.round(calculatedLotSize * 1000) / 1000);
      
      console.log('💰 CRYPTO LOT SIZE CALCULATED:', { 
        entry, 
        stopLoss, 
        slDistance,
        slPercent: (slPercent * 100).toFixed(2) + '%',
        positionSizeUSD: positionSizeUSD.toFixed(2),
        validRisk,
        calculatedLotSize, 
        newLotSize,
        formula: `Risk ${validRisk} / SL% ${(slPercent * 100).toFixed(2)}% = $${positionSizeUSD.toFixed(2)} position / $${entry} entry = ${newLotSize} crypto`
      });
      
      setLotSize(newLotSize);
      console.log('✅ CRYPTO LOT SIZE UPDATED TO:', newLotSize);
      return;
    }
    
    // FOREX PAIRS - Pip-based calculation
    console.log('💱 FOREX PAIR DETECTED:', pair);
    const pipMultiplier = pair.includes('JPY') ? 0.01 : 0.0001;
    const slPips = slDistance / pipMultiplier;
    
    console.log('📏 SL DISTANCE:', { slDistance, slPips, pipMultiplier });
    
    // Pip value calculation
    let pipValue;
    const quoteCurrency = pair.substring(3, 6);
    const baseCurrency = pair.substring(0, 3);
    
    if (quoteCurrency === 'USD') {
      pipValue = pipMultiplier * 100000; // Standard lot
    } else if (baseCurrency === 'USD') {
      pipValue = (pipMultiplier / entry) * 100000;
    } else {
      pipValue = 10; // Approximation for cross pairs
    }
    
    console.log('💵 PIP VALUE:', { pipValue, quoteCurrency, baseCurrency });
    
    // Calculate lot size using proper formula (matches FTMO calculator)
    // Formula: Lot Size = Risk Amount / (SL in Pips × Pip Value)
    const pipValuePerStandardLot = pipValue;
    const calculatedLotSize = validRisk / (slPips * pipValuePerStandardLot);
    const newLotSize = Math.max(0.01, Math.round(calculatedLotSize * 100) / 100);
    
    console.log('💰 FOREX LOT SIZE CALCULATED:', { 
      entry, 
      stopLoss, 
      slDistance,
      slPips, 
      pipValue,
      pipValuePerStandardLot,
      riskAmount,
      validRisk,
      calculatedLotSize, 
      newLotSize
    });
    
    setLotSize(newLotSize);
    console.log('✅ FOREX LOT SIZE UPDATED TO:', newLotSize);
  }, [entry, stopLoss, riskAmount, pair]);
  
  // Update price lines when values change (from non-drag sources like input fields)
  useEffect(() => {
    // Skip if currently dragging (we update directly in handleMouseMove)
    if (isDraggingRef.current) return;
    
    if (entryLineRef.current) {
      entryLineRef.current.applyOptions({ price: entry });
      console.log('📍 Updated Entry line to:', entry);
    }
    if (slLineRef.current) {
      slLineRef.current.applyOptions({ price: stopLoss });
      console.log('🛑 Updated SL line to:', stopLoss);
    }
    if (tpLineRef.current) {
      tpLineRef.current.applyOptions({ price: takeProfit });
      console.log('🎯 Updated TP line to:', takeProfit);
    }
  }, [entry, stopLoss, takeProfit]);
  
  // Create/remove scalp lines when scalp trade is toggled
  useEffect(() => {
    if (!seriesRef.current) return;
    
    // ALWAYS remove existing scalp lines first to prevent duplicates
    if (scalpEntryLineRef.current && seriesRef.current) {
      seriesRef.current.removePriceLine(scalpEntryLineRef.current);
      scalpEntryLineRef.current = null;
    }
    if (scalpSLLineRef.current && seriesRef.current) {
      seriesRef.current.removePriceLine(scalpSLLineRef.current);
      scalpSLLineRef.current = null;
    }
    if (scalpTPLineRef.current && seriesRef.current) {
      seriesRef.current.removePriceLine(scalpTPLineRef.current);
      scalpTPLineRef.current = null;
    }
    
    // Only create new scalp lines if showScalpTrade is true
    if (showScalpTrade && scalpEntry && scalpSL && scalpTP) {
      // Create scalp lines with dashed style
      scalpEntryLineRef.current = seriesRef.current.createPriceLine({
        price: scalpEntry,
        color: '#a855f7', // Purple for scalp entry
        lineWidth: 2,
        lineStyle: 2, // Dashed
        axisLabelVisible: true,
        title: 'Scalp Entry',
      });
      
      scalpSLLineRef.current = seriesRef.current.createPriceLine({
        price: scalpSL,
        color: '#f97316', // Orange for scalp SL
        lineWidth: 2,
        lineStyle: 2, // Dashed
        axisLabelVisible: true,
        title: 'Scalp SL',
      });
      
      scalpTPLineRef.current = seriesRef.current.createPriceLine({
        price: scalpTP,
        color: '#10b981', // Emerald for scalp TP
        lineWidth: 2,
        lineStyle: 2, // Dashed
        axisLabelVisible: true,
        title: 'Scalp TP',
      });
      
      console.log('Scalp lines created on chart');
    }
  }, [showScalpTrade, scalpEntry, scalpSL, scalpTP]);
  
  // Update scalp lines when scalp values change
  useEffect(() => {
    if (scalpEntryLineRef.current && seriesRef.current && showScalpTrade) {
      scalpEntryLineRef.current.applyOptions({ price: scalpEntry });
    }
    if (scalpSLLineRef.current && seriesRef.current && showScalpTrade) {
      scalpSLLineRef.current.applyOptions({ price: scalpSL });
    }
    if (scalpTPLineRef.current && seriesRef.current && showScalpTrade) {
      scalpTPLineRef.current.applyOptions({ price: scalpTP });
    }
  }, [scalpEntry, scalpSL, scalpTP, showScalpTrade]);
  
  // Handle chart resize when entering/exiting fullscreen
  useEffect(() => {
    if (chartRef.current) {
      setTimeout(() => {
        if (isFullscreen) {
          // Resize chart to fullscreen
          chartRef.current.applyOptions({
            width: window.innerWidth,
            height: window.innerHeight - 120
          });
        } else {
          // Resize chart to normal size
          if (chartContainerRef.current) {
            chartRef.current.applyOptions({
              width: chartContainerRef.current.clientWidth,
              height: 500
            });
          }
        }
      }, 100);
    }
  }, [isFullscreen]);
  
  useEffect(() => {
    if (!chartContainerRef.current) return;
    
    console.log(`🔄 Creating chart for ${pair} with timeframe ${timeframe}`);
    
    let chart;
    let resizeHandler;
    let handleChartMouseUp;
    
    // Dynamic import for production compatibility
    import('lightweight-charts')
      .then(({ createChart, CandlestickSeries }) => {
        // Create chart - MT5 STYLE
        chart = createChart(chartContainerRef.current, {
          width: chartContainerRef.current.clientWidth,
          height: 500, // MT5-STYLE: Taller chart for better candle visibility
          layout: {
            background: { color: '#ffffff' },
            textColor: '#333',
          },
          grid: {
            vertLines: { 
              color: '#e0e0e0',
              style: 1, // Solid gridlines
              visible: true,
            },
            horzLines: { 
              color: '#e0e0e0',
              style: 1, // Solid gridlines
              visible: true,
            },
          },
          crosshair: {
            mode: 1,
          },
          rightPriceScale: {
            borderColor: '#cccccc',
            autoScale: true,
          },
          timeScale: {
            borderColor: '#cccccc',
            timeVisible: true,
            secondsVisible: false,
            barSpacing: 25, // MT5-STYLE: MUCH wider spacing for thick, bold candles
            minBarSpacing: 15, // Minimum spacing to keep candles thick
          },
          // CRITICAL: Configure to show DATE + TIME like MT5
          localization: {
            timeFormatter: (timestamp) => {
              const date = new Date(timestamp * 1000);
              const day = date.getDate();
              const month = date.toLocaleString('en-US', { month: 'short' });
              const hours = date.getHours().toString().padStart(2, '0');
              const minutes = date.getMinutes().toString().padStart(2, '0');
              return `${day} ${month} ${hours}:${minutes}`;
            },
          },
          // Enable touch interactions for mobile pinch-to-zoom
          handleScroll: {
            mouseWheel: true,
            pressedMouseMove: true,
            horzTouchDrag: true,
            vertTouchDrag: true,
          },
          handleScale: {
            axisPressedMouseMove: true,
            mouseWheel: true,
            pinch: true, // Enable pinch-to-zoom on mobile!
          },
        });
        
        chartRef.current = chart;
        
        // Create candlestick series - MT5 STYLE (thick, bold, solid bodies)
        const candlestickSeries = chart.addSeries(CandlestickSeries, {
          // MT5 EXACT Colors - Teal/cyan green and dark red
          upColor: '#26a69a',      // MT5 teal/cyan green
          downColor: '#ef5350',    // MT5 red
          borderVisible: false,    // Solid bodies, no borders (critical for thick look)
          wickUpColor: '#26a69a',  // Matching teal wicks
          wickDownColor: '#ef5350', // Matching red wicks
          wickVisible: true,       // Show wicks (thinner than body)
          // MT5-style price precision
          priceFormat: {
            type: 'price',
            precision: 5,
            minMove: 0.00001,
          },
        });
        
        seriesRef.current = candlestickSeries;
        
        // Generate sample data with SEEDED randomness for consistency
        const generateSampleData = () => {
          const data = [];
          let basePrice;
          
          // Use initialEntry as the base price if available
          if (initialEntry && !isNaN(initialEntry)) {
            basePrice = initialEntry;
          } else if (initialSL && initialTP && !isNaN(initialSL) && !isNaN(initialTP)) {
            basePrice = (initialSL + initialTP) / 2; // Use midpoint of SL and TP
          } else {
            basePrice = 1.1000; // Fallback
          }
          
          // SEEDED RANDOM: Use pair name AND timeframe as seed for consistent chart per pair+timeframe
          let seed = 12345; // Default seed
          const seedString = `${pair}-${timeframe}`; // CRITICAL: Include timeframe in seed
          if (seedString) {
            // Simple hash of pair+timeframe to create consistent seed
            for (let i = 0; i < seedString.length; i++) {
              seed = seed * 31 + seedString.charCodeAt(i);
            }
          }
          console.log(`🎲 Chart seed for ${pair} @ ${timeframe}: ${seed}`);
          
          // Seeded random function for consistent results
          const seededRandom = () => {
            seed = (seed * 9301 + 49297) % 233280;
            return seed / 233280;
          };
          
          let currentPrice = basePrice;
          const now = Math.floor(Date.now() / 1000);
          
          // MT5-STYLE: Show fewer candles (30 instead of 50) for THICK, BOLD appearance like MT5
          // CRITICAL: Each candle's OPEN = previous candle's CLOSE (creates connected MT5 appearance)
          for (let i = 30; i >= 0; i--) {
            const time = now - (i * 300);
            
            // Open at the current price (which is the previous candle's close)
            const open = currentPrice;
            
            // Generate high and low around the open using SEEDED random
            const volatility = 0.0003 * basePrice;
            const high = open + seededRandom() * volatility;
            const low = open - seededRandom() * volatility;
            
            // Close is random between low and high
            const close = low + seededRandom() * (high - low);
            
            // CRITICAL: Set currentPrice to this candle's close
            // This ensures next candle opens where this one closes (MT5 style!)
            currentPrice = close;
            
            // Ensure all values are valid numbers
            if (!isNaN(open) && !isNaN(high) && !isNaN(low) && !isNaN(close)) {
              data.push({
                time,
                open: Number(open.toFixed(5)),
                high: Number(high.toFixed(5)),
                low: Number(low.toFixed(5)),
                close: Number(close.toFixed(5)),
              });
            }
          }
          
          return data;
        };
        
        const sampleData = generateSampleData();
        console.log(`📊 Generated ${sampleData.length} sample candles for ${pair} @ ${timeframe}`);
        candlestickSeries.setData(sampleData);
        
        // Debug: Check initial values
        console.log('Creating price lines:', {
          entry: initialEntry,
          sl: initialSL,
          tp: initialTP
        });
        
        // Add price lines for Entry, SL, and TP - only if values are valid
        if (initialEntry && !isNaN(initialEntry)) {
          entryLineRef.current = candlestickSeries.createPriceLine({
            price: initialEntry,
            color: '#3b82f6',
            lineWidth: 3,
            lineStyle: 0, // Solid
            axisLabelVisible: true,
            title: 'Entry',
          });
          console.log('Entry line created');
        }
        
        if (initialSL && !isNaN(initialSL)) {
          slLineRef.current = candlestickSeries.createPriceLine({
            price: initialSL,
            color: '#ef4444',
            lineWidth: 3,
            lineStyle: 0,
            axisLabelVisible: true,
            title: 'SL',
          });
          console.log('SL line created');
        }
        
        if (initialTP && !isNaN(initialTP)) {
          tpLineRef.current = candlestickSeries.createPriceLine({
            price: initialTP,
            color: '#22c55e',
            lineWidth: 3,
            lineStyle: 0,
            axisLabelVisible: true,
            title: 'TP',
          });
          console.log('TP line created');
        }
        
        // Fit content and set initial visible range to show ALL price lines
        chart.timeScale().fitContent();
        
        // Calculate visible range to include Entry, SL, and TP
        if (initialEntry && initialSL && initialTP) {
          // Find min and max of ALL important prices (lines + candles)
          const dataMin = Math.min(...sampleData.map(d => d.low));
          const dataMax = Math.max(...sampleData.map(d => d.high));
          
          // Include all price lines in the range
          const allPrices = [initialEntry, initialSL, initialTP, dataMin, dataMax];
          const minPrice = Math.min(...allPrices);
          const maxPrice = Math.max(...allPrices);
          const priceRange = maxPrice - minPrice;
          
          // Add 20% padding on top and bottom
          const padding = priceRange * 0.2;
          const visibleMin = minPrice - padding;
          const visibleMax = maxPrice + padding;
          
          // Use autoscaleInfoProvider to force this range
          candlestickSeries.applyOptions({
            autoscaleInfoProvider: () => ({
              priceRange: {
                minValue: visibleMin,
                maxValue: visibleMax,
              },
            }),
          });
          
          console.log('Initial view set to show ALL lines:');
          console.log('  Visible range:', visibleMin.toFixed(2), '-', visibleMax.toFixed(2));
          console.log('  Entry:', initialEntry, 'SL:', initialSL, 'TP:', initialTP);
        }
        
        // Add interactive dragging for price lines using direct mouse events
        const container = chartContainerRef.current;
        
        const handleMouseDown = (e) => {
          console.log('Mouse down on chart');
          const rect = container.getBoundingClientRect();
          const y = e.clientY - rect.top;
          const price = candlestickSeries.coordinateToPrice(y);
          
          if (!price || isNaN(price)) {
            console.log('Invalid price:', price);
            return;
          }
          
          console.log('Clicked at price:', price);
          
          // Use PIXEL-based tolerance instead of price-based (10 pixels)
          const pixelTolerance = 10;
          
          // Convert price lines to pixel coordinates
          const entryPixel = candlestickSeries.priceToCoordinate(entryRef.current);
          const slPixel = candlestickSeries.priceToCoordinate(stopLossRef.current);
          const tpPixel = candlestickSeries.priceToCoordinate(takeProfitRef.current);
          
          console.log('Click Y:', y, 'Entry Y:', entryPixel, 'SL Y:', slPixel, 'TP Y:', tpPixel);
          
          // Check main trade lines using PIXEL distances
          const pixelDistances = {
            entry: Math.abs(y - entryPixel),
            sl: Math.abs(y - slPixel),
            tp: Math.abs(y - tpPixel)
          };
          
          console.log('Pixel distances:', pixelDistances);
          
          // Find the closest line
          const closestLine = Object.entries(pixelDistances).reduce((min, [line, dist]) => 
            dist < min.dist ? { line, dist } : min, 
            { line: null, dist: Infinity }
          );
          
          // ONLY grab if click is within 10 pixels of the line
          if (closestLine.dist < pixelTolerance) {
            isDraggingRef.current = closestLine.line;
            console.log(`✅ Grabbed ${closestLine.line} line (pixel distance: ${closestLine.dist.toFixed(2)}px)`);
            container.style.cursor = 'grabbing';
            
            // Prevent chart's default drag behavior (check if methods exist)
            if (e.stopPropagation) e.stopPropagation();
            if (e.preventDefault) e.preventDefault();
          }
          // Check scalp trade lines if visible
          else if (showScalpTradeRef.current) {
            const scalpEntryPixel = candlestickSeries.priceToCoordinate(scalpEntryRef.current);
            const scalpSLPixel = candlestickSeries.priceToCoordinate(scalpSLRef.current);
            const scalpTPPixel = candlestickSeries.priceToCoordinate(scalpTPRef.current);
            
            const scalpPixelDistances = {
              scalpEntry: Math.abs(y - scalpEntryPixel),
              scalpSL: Math.abs(y - scalpSLPixel),
              scalpTP: Math.abs(y - scalpTPPixel)
            };
            
            const closestScalp = Object.entries(scalpPixelDistances).reduce((min, [line, dist]) => 
              dist < min.dist ? { line, dist } : min, 
              { line: null, dist: Infinity }
            );
            
            if (closestScalp.dist < pixelTolerance) {
              isDraggingRef.current = closestScalp.line;
              console.log(`✅ Grabbed ${closestScalp.line} line (pixel distance: ${closestScalp.dist.toFixed(2)}px)`);
              container.style.cursor = 'grabbing';
              
              // Prevent chart's default drag behavior (check if methods exist)
              if (e.stopPropagation) e.stopPropagation();
              if (e.preventDefault) e.preventDefault();
            } else {
              console.log('❌ No line grabbed - all distances > 10px');
            }
          } else {
            console.log('❌ No line grabbed - all distances > 10px');
          }
        };
        
        const handleMouseMove = (e) => {
          const rect = container.getBoundingClientRect();
          const y = e.clientY - rect.top;
          const price = candlestickSeries.coordinateToPrice(y);
          
          if (!price || isNaN(price)) return;
          
          // If dragging, update the price
          if (isDraggingRef.current) {
            // Prevent chart's default pan behavior (check if methods exist)
            if (e.stopPropagation) e.stopPropagation();
            if (e.preventDefault) e.preventDefault();
            
            const roundedPrice = Number(price.toFixed(5));
            console.log('Dragging', isDraggingRef.current, 'to price:', roundedPrice);
            
            // Update the price line using applyOptions (correct method per v5 docs)
            if (isDraggingRef.current === 'entry') {
              if (entryLineRef.current) {
                entryLineRef.current.applyOptions({ price: roundedPrice });
                console.log('🔵 Entry line updated to:', roundedPrice);
              } else {
                console.warn('⚠️ Entry line ref is null!');
              }
              entryRef.current = roundedPrice;
              setEntry(roundedPrice);
            } else if (isDraggingRef.current === 'sl') {
              if (slLineRef.current) {
                slLineRef.current.applyOptions({ price: roundedPrice });
                console.log('🔴 SL line updated to:', roundedPrice);
              } else {
                console.warn('⚠️ SL line ref is null!');
              }
              stopLossRef.current = roundedPrice;
              setStopLoss(roundedPrice);
            } else if (isDraggingRef.current === 'tp') {
              if (tpLineRef.current) {
                tpLineRef.current.applyOptions({ price: roundedPrice });
                console.log('🟢 TP line updated to:', roundedPrice);
              } else {
                console.warn('⚠️ TP line ref is null!');
              }
              takeProfitRef.current = roundedPrice;
              setTakeProfit(roundedPrice);
            } else if (isDraggingRef.current === 'scalpEntry') {
              if (scalpEntryLineRef.current) {
                scalpEntryLineRef.current.applyOptions({ price: roundedPrice });
              }
              scalpEntryRef.current = roundedPrice;
              setScalpEntry(roundedPrice);
            } else if (isDraggingRef.current === 'scalpSL') {
              if (scalpSLLineRef.current) {
                scalpSLLineRef.current.applyOptions({ price: roundedPrice });
              }
              scalpSLRef.current = roundedPrice;
              setScalpSL(roundedPrice);
            } else if (isDraggingRef.current === 'scalpTP') {
              if (scalpTPLineRef.current) {
                scalpTPLineRef.current.applyOptions({ price: roundedPrice });
              }
              scalpTPRef.current = roundedPrice;
              setScalpTP(roundedPrice);
            }
          } else {
            // Show hover cursor when near a line (pixel-based)
            const pixelTolerance = 10;
            const entryPixel = candlestickSeries.priceToCoordinate(entryRef.current);
            const slPixel = candlestickSeries.priceToCoordinate(stopLossRef.current);
            const tpPixel = candlestickSeries.priceToCoordinate(takeProfitRef.current);
            
            const rect = container.getBoundingClientRect();
            const y = e.clientY - rect.top;
            
            const nearLine = (
              Math.abs(y - entryPixel) < pixelTolerance ||
              Math.abs(y - slPixel) < pixelTolerance ||
              Math.abs(y - tpPixel) < pixelTolerance
            );
            
            container.style.cursor = nearLine ? 'grab' : 'crosshair';
          }
        };
        
        handleChartMouseUp = () => {
          if (isDraggingRef.current) {
            console.log('Released', isDraggingRef.current, 'line');
          }
          isDraggingRef.current = null;
          container.style.cursor = 'crosshair';
        };
        
        // Add event listeners to container
        container.addEventListener('mousedown', handleMouseDown);
        container.addEventListener('mousemove', handleMouseMove);
        container.addEventListener('mouseup', handleChartMouseUp);
        container.addEventListener('mouseleave', handleChartMouseUp);
        
        // Touch events for mobile
        container.addEventListener('touchstart', (e) => {
          const touch = e.touches[0];
          handleMouseDown({ clientY: touch.clientY });
        });
        container.addEventListener('touchmove', (e) => {
          const touch = e.touches[0];
          handleMouseMove({ clientY: touch.clientY });
          e.preventDefault();
        });
        container.addEventListener('touchend', handleChartMouseUp);
        
        // Store event handlers for cleanup
        container._handleMouseDown = handleMouseDown;
        container._handleMouseMove = handleMouseMove;
        container._handleMouseUp = handleChartMouseUp;
        container._handleTouchStart = (e) => {
          const touch = e.touches[0];
          handleMouseDown({ clientY: touch.clientY });
        };
        container._handleTouchMove = (e) => {
          const touch = e.touches[0];
          handleMouseMove({ clientY: touch.clientY });
          e.preventDefault();
        };
        
        // Handle resize
        resizeHandler = () => {
          if (chartContainerRef.current && chart) {
            chart.applyOptions({
              width: chartContainerRef.current.clientWidth,
            });
          }
        };
        
        window.addEventListener('resize', resizeHandler);
      })
      .catch((error) => {
        console.error('Failed to load chart library:', error);
      });
    
    return () => {
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
      }
      
      // CRITICAL FIX: Remove all event listeners before chart recreation
      if (chartContainerRef.current) {
        const container = chartContainerRef.current;
        // Safely remove each listener with null checks
        if (container._handleMouseDown) {
          container.removeEventListener('mousedown', container._handleMouseDown);
        }
        if (container._handleMouseMove) {
          container.removeEventListener('mousemove', container._handleMouseMove);
        }
        if (container._handleMouseUp) {
          container.removeEventListener('mouseup', container._handleMouseUp);
          container.removeEventListener('mouseleave', container._handleMouseUp);
          container.removeEventListener('touchend', container._handleMouseUp);
        }
        if (container._handleTouchStart) {
          container.removeEventListener('touchstart', container._handleTouchStart);
        }
        if (container._handleTouchMove) {
          container.removeEventListener('touchmove', container._handleTouchMove);
        }
        console.log('🧹 Cleaned up event listeners for chart recreation');
      }
      
      if (chart) {
        chart.remove();
      }
      // Clear price line refs
      entryLineRef.current = null;
      slLineRef.current = null;
      tpLineRef.current = null;
      scalpEntryLineRef.current = null;
      scalpSLLineRef.current = null;
      scalpTPLineRef.current = null;
    };
  }, [pair, timeframe]); // Recreate chart when pair OR timeframe changes
  
  const handleConfirm = () => {
    onConfirm({
      entry,
      stopLoss,
      takeProfit,
      lotSize
    });
  };
  
  const slDistance = Math.abs(entry - stopLoss);
  const tpDistance = Math.abs(entry - takeProfit);
  const riskRewardRatio = tpDistance / slDistance;
  
  return (
    <>
      {/* Chart Container - Shared between fullscreen and normal view */}
      <div 
        ref={chartContainerRef} 
        style={{ 
          position: isFullscreen ? 'fixed' : 'relative',
          top: isFullscreen ? 0 : 'auto',
          left: isFullscreen ? 0 : 'auto',
          right: isFullscreen ? 0 : 'auto',
          width: isFullscreen ? '100%' : '100%',
          height: isFullscreen ? 'calc(100vh - 120px)' : '500px',
          zIndex: isFullscreen ? 9998 : 1,
          background: '#ffffff',
          marginBottom: isFullscreen ? 0 : '1.5rem',
          borderRadius: isFullscreen ? 0 : '12px',
          overflow: 'hidden',
          border: isFullscreen ? 'none' : '2px solid rgba(8, 145, 178, 0.2)',
          touchAction: 'none' // Allow all touch gestures including pinch-to-zoom
        }}
      />

      {/* Countdown Timer Overlay */}
      <div style={{
        position: isFullscreen ? 'fixed' : 'absolute',
        top: isFullscreen ? '10px' : '10px',
        right: isFullscreen ? '10px' : '10px',
        zIndex: isFullscreen ? 9999 : 10,
        background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
        color: 'white',
        padding: '0.75rem 1.25rem',
        borderRadius: '8px',
        fontWeight: '700',
        fontSize: '0.875rem',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        border: '2px solid rgba(255,255,255,0.3)'
      }}>
        <span style={{ fontSize: '1rem' }}>⏱️</span>
        <span>{countdown || 'Calculating...'}</span>
      </div>


      {/* Fullscreen Control Buttons */}
      {isFullscreen && (
        <div 
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: '120px',
            background: '#ffffff',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
            margin: 0
          }}
        >

          {/* Fullscreen Control Buttons - Tick and Cross */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '2rem',
            padding: '1rem',
            background: '#ffffff'
          }}>
            {/* Cross Button - Exit Fullscreen */}
            <button
              onClick={() => setIsFullscreen(false)}
              style={{
                width: '80px',
                height: '80px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '3px solid #ef4444',
                borderRadius: '50%',
                fontSize: '2.5rem',
                color: '#ef4444',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                fontWeight: '700'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#ef4444';
                e.target.style.color = '#ffffff';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                e.target.style.color = '#ef4444';
                e.target.style.transform = 'scale(1)';
              }}
            >
              ✕
            </button>

            {/* Tick Button - Confirm Trade */}
            <button
              onClick={() => {
                setIsFullscreen(false);
                onConfirm({
                  entry,
                  stopLoss,
                  takeProfit,
                  lotSize
                });
              }}
              style={{
                width: '80px',
                height: '80px',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '3px solid #22c55e',
                borderRadius: '50%',
                fontSize: '2.5rem',
                color: '#22c55e',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                fontWeight: '700'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#22c55e';
                e.target.style.color = '#ffffff';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(34, 197, 94, 0.1)';
                e.target.style.color = '#22c55e';
                e.target.style.transform = 'scale(1)';
              }}
            >
              ✓
            </button>
          </div>
        </div>
      )}

      {/* Normal View */}
      <div 
        style={{
          background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '16px',
          padding: '1.5rem',
          maxWidth: '900px',
          margin: '2rem auto',
          boxShadow: '0 8px 30px rgba(8, 145, 178, 0.15)',
          display: isFullscreen ? 'none' : 'block'
        }}
      >
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1rem' 
      }}>
        <h2 style={{ 
          fontSize: '1.5rem', 
          fontWeight: '700',
          color: '#0f172a',
          margin: 0
        }}>
          📊 {pair} - Adjust Your Trade
        </h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {/* Fullscreen Button */}
          <button
            onClick={() => setIsFullscreen(true)}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(8, 145, 178, 0.1)',
              border: '2px solid #0891b2',
              borderRadius: '8px',
              fontWeight: '700',
              color: '#0891b2',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#0891b2';
              e.target.style.color = '#ffffff';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(8, 145, 178, 0.1)';
              e.target.style.color = '#0891b2';
            }}
          >
            ⛶ Fullscreen
          </button>
          <div style={{
            padding: '0.5rem 1rem',
            background: direction === 'BUY' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            border: `2px solid ${direction === 'BUY' ? '#22c55e' : '#ef4444'}`,
            borderRadius: '8px',
            fontWeight: '700',
            color: direction === 'BUY' ? '#16a34a' : '#dc2626'
          }}>
            {direction}
          </div>
        </div>
      </div>
      
      <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>
        Drag the lines to adjust entry, stop loss, and take profit. Lot size updates automatically.
      </div>
      
      {/* Chart is rendered at the top level - shared between fullscreen and normal view */}
      
      {/* Recommendation Cards - Show context while adjusting */}
      {priceAction && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          
          {/* Price Action Context */}
          <div style={{
            background: priceAction.context.includes("Enter Now") ? "rgba(34, 197, 94, 0.1)" : priceAction.context.includes("Set Limit") ? "rgba(59, 130, 246, 0.1)" : "rgba(148, 163, 184, 0.1)",
            padding: '1.5rem',
            borderRadius: '12px',
            border: `2px solid ${priceAction.context.includes("Enter Now") ? "#22c55e" : priceAction.context.includes("Set Limit") ? "#3b82f6" : "#94a3b8"}`
          }}>
            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>
              Price Action Context
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: priceAction.context.includes("Enter Now") ? "#22c55e" : "#3b82f6", marginBottom: '0.5rem' }}>
              {priceAction.context}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#334155' }}>
              {priceAction.reason}
            </div>
          </div>
          
          {/* Entry Action */}
          <div style={{
            background: priceAction.recommendation === "Market Order" ? "rgba(34, 197, 94, 0.1)" : "rgba(59, 130, 246, 0.1)",
            padding: '1.5rem',
            borderRadius: '12px',
            border: `3px solid ${priceAction.recommendation === "Market Order" ? "#22c55e" : "#3b82f6"}`
          }}>
            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>
              Order Type
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: priceAction.recommendation === "Market Order" ? "#16a34a" : "#3b82f6", marginBottom: '0.5rem' }}>
              {priceAction.recommendation}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#334155', marginBottom: '0.75rem' }}>
              Confidence: {priceAction.confidence}%
            </div>
            
            {/* Scalp Button - Show if Limit Order */}
            {priceAction.recommendation.includes("Limit") && counterTradeInfo && (
              <button
                onClick={() => {
                  if (!showScalpTrade) {
                    // Initialize scalp values from counter trade info
                    setScalpEntry(counterTradeInfo.entry);
                    setScalpTP(counterTradeInfo.tp);
                    // Calculate tight SL (10 pips from entry)
                    const pipSize = pair.includes('JPY') ? 0.01 : 0.0001;
                    const slDistance = pipSize * 10;
                    const calculatedSL = counterTradeInfo.action === "SELL NOW" 
                      ? counterTradeInfo.entry + slDistance
                      : counterTradeInfo.entry - slDistance;
                    setScalpSL(calculatedSL);
                    setScalpLotSize(lotSize);
                  }
                  setShowScalpTrade(!showScalpTrade);
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  marginTop: '0.5rem',
                  background: 'linear-gradient(135deg, #eab308 0%, #f59e0b 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                💰 {showScalpTrade ? 'Hide' : 'Add'} Scalp Trade
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Price Lines Controls */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {/* Entry Price */}
        <div 
          style={{
            padding: '1rem',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            userSelect: 'none'
          }}
        >
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
            📍 Entry Price
          </div>
          <input
            type="number"
            value={entry}
            onChange={(e) => setEntry(parseFloat(e.target.value) || entry)}
            step="0.00001"
            style={{
              width: '100%',
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#3b82f6',
              border: 'none',
              background: 'transparent',
              outline: 'none'
            }}
          />
        </div>
        
        {/* Stop Loss */}
        <div 
          style={{
            padding: '1rem',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '2px solid #ef4444',
            borderRadius: '12px',
            userSelect: 'none'
          }}
        >
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
            🛑 Stop Loss
          </div>
          <input
            type="number"
            value={stopLoss}
            onChange={(e) => setStopLoss(parseFloat(e.target.value) || stopLoss)}
            step="0.00001"
            style={{
              width: '100%',
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#ef4444',
              border: 'none',
              background: 'transparent',
              outline: 'none'
            }}
          />
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
            Distance: {(Math.abs((entry - stopLoss) / (pair.includes('JPY') ? 0.01 : 0.0001))).toFixed(1)} pips
          </div>
        </div>
        
        {/* Take Profit */}
        <div 
          style={{
            padding: '1rem',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '2px solid #22c55e',
            borderRadius: '12px',
            userSelect: 'none'
          }}
        >
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
            🎯 Take Profit
          </div>
          <input
            type="number"
            value={takeProfit}
            onChange={(e) => setTakeProfit(parseFloat(e.target.value) || takeProfit)}
            step="0.00001"
            style={{
              width: '100%',
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#22c55e',
              border: 'none',
              background: 'transparent',
              outline: 'none'
            }}
          />
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
            Distance: {(Math.abs((entry - takeProfit) / (pair.includes('JPY') ? 0.01 : 0.0001))).toFixed(1)} pips
          </div>
        </div>
      </div>
      
      {/* Trade Summary */}
      <div style={{
        padding: '1.5rem',
        background: 'linear-gradient(135deg, rgba(8, 145, 178, 0.1) 0%, rgba(6, 182, 212, 0.15) 100%)',
        border: '2px solid #0891b2',
        borderRadius: '12px',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
              Lot Size (Auto-Calculated)
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#0891b2' }}>
              {lotSize.toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
              Risk Amount
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#0891b2' }}>
              ${riskAmount}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
              Potential Profit
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#22c55e' }}>
              ${(riskAmount * riskRewardRatio).toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
              Risk:Reward Ratio
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: riskRewardRatio >= 2.5 ? '#22c55e' : '#f59e0b' }}>
              1:{riskRewardRatio.toFixed(2)}
            </div>
          </div>
        </div>
      </div>
      
      {/* Scalp Trade Section - Appears when button clicked */}
      {showScalpTrade && counterTradeInfo && (
        <div style={{
          marginTop: '1.5rem',
          padding: '1.5rem',
          background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.1) 0%, rgba(251, 191, 36, 0.15) 100%)',
          border: '3px solid #eab308',
          borderRadius: '12px'
        }}>
          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#eab308', marginBottom: '1rem' }}>
            💰 Counter-Trend Scalp Trade
          </div>
          <div style={{ fontSize: '0.875rem', color: '#334155', marginBottom: '1rem' }}>
            {counterTradeInfo.message}
          </div>
          
          {/* Scalp Trade Inputs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Scalp Entry</div>
              <input
                type="number"
                value={scalpEntry || counterTradeInfo.entry}
                onChange={(e) => setScalpEntry(parseFloat(e.target.value))}
                step="0.00001"
                style={{ width: '100%', padding: '0.5rem', fontSize: '0.875rem', borderRadius: '6px', border: '1px solid #eab308' }}
              />
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Scalp TP</div>
              <input
                type="number"
                value={scalpTP || counterTradeInfo.tp}
                onChange={(e) => setScalpTP(parseFloat(e.target.value))}
                step="0.00001"
                style={{ width: '100%', padding: '0.5rem', fontSize: '0.875rem', borderRadius: '6px', border: '1px solid #eab308' }}
              />
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Scalp Lot Size</div>
              <input
                type="number"
                value={scalpLotSize || lotSize}
                onChange={(e) => setScalpLotSize(parseFloat(e.target.value))}
                step="0.01"
                style={{ width: '100%', padding: '0.5rem', fontSize: '0.875rem', borderRadius: '6px', border: '1px solid #eab308' }}
              />
            </div>
          </div>
          
          <div style={{ fontSize: '0.75rem', color: '#334155', padding: '0.75rem', background: 'rgba(255,255,255,0.7)', borderRadius: '6px' }}>
            <strong>Strategy:</strong> Enter {counterTradeInfo.action} at {(scalpEntry || counterTradeInfo.entry).toFixed(5)} → TP at {(scalpTP || counterTradeInfo.tp).toFixed(5)}, then enter main limit order
          </div>
        </div>
      )}
      
      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
        <button
          onClick={onCancel}
          style={{
            flex: 1,
            padding: '1rem',
            background: 'rgba(148, 163, 184, 0.2)',
            border: '2px solid rgba(148, 163, 184, 0.5)',
            borderRadius: '12px',
            color: '#334155',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(148, 163, 184, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(148, 163, 184, 0.2)';
          }}
        >
          ← Back to Calculator
        </button>
        <button
          onClick={handleConfirm}
          style={{
            flex: 2,
            padding: '1rem',
            background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
            border: 'none',
            borderRadius: '12px',
            color: 'white',
            fontSize: '1.125rem',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 10px 30px rgba(8, 145, 178, 0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 15px 40px rgba(8, 145, 178, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 10px 30px rgba(8, 145, 178, 0.3)';
          }}
        >
          ✓ Confirm Trade Parameters
        </button>
      </div>
      </div>
    </>
  );
};

export default InteractiveTradingChart;
